import type {
  AdminBulkResponse,
  AdminCompanyBulkAction,
  AdminCompanyCreateRequest,
  AdminCompanyDeletePreview,
  AdminCompanyDto,
  AdminCompanyPatchRequest,
  AdminCompanyPlan,
  AdminCompanyStatus,
} from '../types';

export const ADMIN_COMPANY_PLANS = ['Enterprise', 'Team', 'Trial'] as const;
export const ADMIN_COMPANY_STATUSES = ['active', 'trial', 'suspended'] as const;

const Plans = new Set<AdminCompanyPlan>(ADMIN_COMPANY_PLANS);
const Statuses = new Set<AdminCompanyStatus>(ADMIN_COMPANY_STATUSES);
const SortColumnIds = new Set([
  'name',
  'domain',
  'plan',
  'users',
  'licenses',
  'country',
  'created',
  'status',
]);
const SortQueryToColumn: Record<string, string> = {
  createdAt: 'created',
  licensesCount: 'licenses',
  usersCount: 'users',
};
const SortColumnToQuery: Record<string, string> = {
  created: 'createdAt',
  licenses: 'licensesCount',
  users: 'usersCount',
};

export interface CompanyTableFilters {
  [key: string]: string;
  name: string;
  domain: string;
  plan: string;
  country: string;
  status: string;
}

export interface CompanyTableQueryState {
  filters: CompanyTableFilters;
  sort: { columnId: string; direction: 'ascending' | 'descending' } | null;
}

export interface CompanyDraft {
  name: string;
  domain: string;
  plan: AdminCompanyPlan;
  country: string;
  contactEmail: string;
  status: AdminCompanyStatus;
  notes: string;
}

export type CompanyDraftErrors = Partial<Record<keyof CompanyDraft, string>>;

export const parseCompanyTableQuery = (query: URLSearchParams): CompanyTableQueryState => {
  const rawPlan = query.get('plan') ?? '';
  const rawStatus = query.get('status') ?? '';
  const rawSort = query.get('sort') ?? '';
  const descending = rawSort.startsWith('-');
  const querySortId = descending ? rawSort.slice(1) : rawSort;
  const sortColumnId = SortQueryToColumn[querySortId] ?? querySortId;

  return {
    filters: {
      name: query.get('name') ?? '',
      domain: query.get('domain') ?? '',
      plan: Plans.has(rawPlan as AdminCompanyPlan) ? rawPlan : '',
      country: query.get('country') ?? '',
      status: Statuses.has(rawStatus as AdminCompanyStatus) ? rawStatus : '',
    },
    sort: SortColumnIds.has(sortColumnId)
      ? { columnId: sortColumnId, direction: descending ? 'descending' : 'ascending' }
      : null,
  };
};

export const serializeCompanyTableQuery = (
  state: CompanyTableQueryState
): URLSearchParams => {
  const query = new URLSearchParams();
  (['name', 'domain', 'plan', 'country', 'status'] as const).forEach((key) => {
    if (state.filters[key] !== '') {
      query.set(key, state.filters[key]);
    }
  });
  if (state.sort) {
    const prefix = state.sort.direction === 'descending' ? '-' : '';
    const queryColumn = SortColumnToQuery[state.sort.columnId] ?? state.sort.columnId;
    query.set('sort', `${prefix}${queryColumn}`);
  }
  return query;
};

export const isValidCompanyDomain = (value: string): boolean => {
  const domain = value.trim().toLowerCase();
  if (domain.length === 0 || domain.length > 253 || domain.includes('..')) {
    return false;
  }
  return domain.split('.').length > 1 && domain.split('.').every((label) =>
    label.length > 0
    && label.length <= 63
    && !label.startsWith('-')
    && !label.endsWith('-')
    && /^[a-z0-9-]+$/i.test(label));
};

export const isValidCompanyEmail = (value: string): boolean => {
  const email = value.trim();
  if (email.length === 0 || email.length > 320 || /[\r\n]/.test(email)) {
    return false;
  }
  const [local, domain, extra] = email.split('@');
  return extra === undefined
    && Boolean(local)
    && local.length <= 64
    && !local.startsWith('.')
    && !local.endsWith('.')
    && !local.includes('..')
    && /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(local)
    && isValidCompanyDomain(domain ?? '');
};

export const createCompanyDraft = (company: AdminCompanyDto): CompanyDraft => ({
  name: company.name,
  domain: company.domain,
  plan: company.plan,
  country: company.country,
  contactEmail: company.contactEmail,
  status: company.status,
  notes: company.notes,
});

export const createNewCompanyDraft = (): CompanyDraft => ({
  name: '',
  domain: '',
  plan: 'Trial',
  country: '',
  contactEmail: '',
  status: 'trial',
  notes: '',
});

export const validateCompanyDraft = (draft: CompanyDraft): CompanyDraftErrors => {
  const errors: CompanyDraftErrors = {};
  if (draft.name.trim() === '') {
    errors.name = 'Company name is required.';
  }
  if (!isValidCompanyDomain(draft.domain)) {
    errors.domain = 'Enter a valid company domain.';
  }
  if (!Plans.has(draft.plan)) {
    errors.plan = 'Choose Enterprise, Team, or Trial.';
  }
  if (draft.country.trim() === '') {
    errors.country = 'Country is required.';
  }
  if (!isValidCompanyEmail(draft.contactEmail)) {
    errors.contactEmail = 'Enter a valid contact email address.';
  }
  if (!Statuses.has(draft.status)) {
    errors.status = 'Choose active, trial, or suspended.';
  }
  return errors;
};

export const isCompanyDraftDirty = (draft: CompanyDraft, original: CompanyDraft): boolean =>
  draft.name !== original.name
  || draft.domain !== original.domain
  || draft.plan !== original.plan
  || draft.country !== original.country
  || draft.contactEmail !== original.contactEmail
  || draft.status !== original.status
  || draft.notes !== original.notes;

const normalizeDraft = (draft: CompanyDraft): AdminCompanyCreateRequest => ({
  name: draft.name.trim(),
  domain: draft.domain.trim().toLowerCase(),
  plan: draft.plan,
  country: draft.country.trim(),
  contactEmail: draft.contactEmail.trim(),
  status: draft.status,
  notes: draft.notes.trim(),
});

export const createCompanyCreateRequest = normalizeDraft;

export const createCompanyPatchRequest = (
  draft: CompanyDraft,
  original: CompanyDraft
): AdminCompanyPatchRequest | null => {
  const normalized = normalizeDraft(draft);
  const normalizedOriginal = normalizeDraft(original);
  const patch: AdminCompanyPatchRequest = {};
  (Object.keys(normalized) as Array<keyof AdminCompanyCreateRequest>).forEach((key) => {
    if (normalized[key] !== normalizedOriginal[key]) {
      patch[key] = normalized[key] as never;
    }
  });
  return Object.keys(patch).length > 0 ? patch : null;
};

export const companyNestedLink = (
  target: 'licenses' | 'users',
  companyId: number
): string => `/super-admin/${target}?companyId=${companyId}`;

const BulkPastTense: Record<AdminCompanyBulkAction, string> = {
  delete: 'Deleted',
  suspend: 'Suspended',
};

const companyCount = (count: number): string => `${count} ${count === 1 ? 'company' : 'companies'}`;

export const formatCompanyBulkResult = (
  action: AdminCompanyBulkAction,
  result: AdminBulkResponse
): string => {
  const succeeded = `${BulkPastTense[action]} ${companyCount(result.succeeded.length)}`;
  if (result.failed.length === 0) {
    return `${succeeded}.`;
  }
  const failures = result.failed.map((failure) => `#${failure.id} ${failure.code}`).join(', ');
  return `${succeeded}; ${result.failed.length} failed: ${failures}.`;
};

export const formatCascadeCounts = (counts: AdminCompanyDeletePreview): string =>
  `${counts.users} ${counts.users === 1 ? 'user' : 'users'} and ${counts.licenses} ${counts.licenses === 1 ? 'license' : 'licenses'}`;
